import { postIa } from "api/iaBackend";
import { URL_DATA } from "customGlobal";
import auth from "auth";
import * as pdfjsLib from "pdfjs-dist";

let pdfWorkerReady = false;

function ensurePdfWorker() {
  if (pdfWorkerReady || typeof window === "undefined") {
    return;
  }

  // Vite émet un chunk .js. Un .mjs copié tel quel est refusé par nginx (module non chargé).
  pdfjsLib.GlobalWorkerOptions.workerPort = new Worker(
    new URL("../../node_modules/pdfjs-dist/build/pdf.worker.min.mjs", import.meta.url),
    { type: "module" }
  );
  pdfWorkerReady = true;
}

export interface DocumentAnalysisResult {
  analysis: string;
  documentId: string;
  documentName: string;
  documentType: string;
  analysisDate: string;
}

const MAX_PAGES = 50;
const OCR_MAX_PAGES = 5;
const OCR_MAX_WIDTH = 2200;
const MAX_FILE_SIZE_BYTES = 20 * 1024 * 1024;

export type DocumentAnalysisMode = "full" | "concise";

function getFileType(nom: string, mimeType: string): string {
  const extension = nom.split(".").pop()?.toLowerCase();
  if (extension && extension !== nom.toLowerCase()) {
    return extension;
  }

  const normalizedMimeType = mimeType.toLowerCase();
  if (normalizedMimeType.includes("pdf")) return "pdf";
  if (normalizedMimeType.includes("wordprocessingml") || normalizedMimeType.includes("msword")) {
    return "docx";
  }
  if (normalizedMimeType.includes("spreadsheetml") || normalizedMimeType.includes("ms-excel")) {
    return "xlsx";
  }
  if (normalizedMimeType.includes("presentationml")) return "pptx";
  if (normalizedMimeType.includes("text/plain")) return "txt";
  if (normalizedMimeType.includes("text/csv")) return "csv";

  return extension || "unknown";
}

const LOCAL_ANALYSIS_TYPES = new Set(["pdf", "docx", "doc"]);

export function isAnalyzableDocumentFile(file: { name: string; type?: string }): boolean {
  return LOCAL_ANALYSIS_TYPES.has(getFileType(file.name, file.type ?? ""));
}

function isPdfBuffer(buffer: ArrayBuffer): boolean {
  if (buffer.byteLength < 4) return false;
  const header = new Uint8Array(buffer, 0, 4);
  return (
    header[0] === 0x25 &&
    header[1] === 0x50 &&
    header[2] === 0x44 &&
    header[3] === 0x46
  );
}

function isZipBuffer(buffer: ArrayBuffer): boolean {
  if (buffer.byteLength < 2) return false;
  const header = new Uint8Array(buffer, 0, 2);
  return header[0] === 0x50 && header[1] === 0x4b;
}

function isOleBuffer(buffer: ArrayBuffer): boolean {
  if (buffer.byteLength < 4) return false;
  const header = new Uint8Array(buffer, 0, 4);
  return header[0] === 0xd0 && header[1] === 0xcf && header[2] === 0x11 && header[3] === 0xe0;
}

function isExtractedContentInsufficient(content: string): boolean {
  const trimmed = content.trim();
  if (!trimmed) return true;
  if (trimmed.length < 50) return true;
  if (trimmed.startsWith("[") && trimmed.includes("]")) return true;
  return false;
}

function getMimeTypeForFileType(fileType: string, fileName: string): string {
  switch (fileType) {
    case "pdf":
      return "application/pdf";
    case "docx":
      return "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
    case "doc":
      return "application/msword";
    case "xlsx":
      return "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
    case "xls":
      return "application/vnd.ms-excel";
    case "pptx":
      return "application/vnd.openxmlformats-officedocument.presentationml.presentation";
    default:
      return "application/octet-stream";
  }
}

type PdfDocument = Awaited<ReturnType<typeof pdfjsLib.getDocument>["promise"]>;

async function readPdfTextLayer(pdf: PdfDocument): Promise<string> {
  let text = "";
  const maxPages = Math.min(pdf.numPages, MAX_PAGES);

  for (let i = 1; i <= maxPages; i++) {
    const page = await pdf.getPage(i);
    const content = await page.getTextContent();
    const strings = content.items
      .map(item => ("str" in item ? item.str : ""))
      .filter(Boolean);
    text += strings.join(" ") + "\n";
  }

  if (pdf.numPages > maxPages) {
    text += `\n[Document tronqué après ${maxPages} pages...]`;
  }

  return text.trim();
}

async function renderPdfPage(pdf: PdfDocument, pageNumber: number): Promise<HTMLCanvasElement | null> {
  if (typeof document === "undefined") return null;

  const page = await pdf.getPage(pageNumber);
  const baseViewport = page.getViewport({ scale: 1 });
  const scale = Math.min(2.5, OCR_MAX_WIDTH / baseViewport.width);
  const viewport = page.getViewport({ scale });
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d", { willReadFrequently: true });
  if (!context) return null;

  canvas.width = Math.ceil(viewport.width);
  canvas.height = Math.ceil(viewport.height);
  context.fillStyle = "#ffffff";
  context.fillRect(0, 0, canvas.width, canvas.height);
  await page.render({
    canvas,
    viewport,
    background: "#ffffff",
    annotationMode: pdfjsLib.AnnotationMode.DISABLE
  }).promise;
  return canvas;
}

async function recognizePdfPages(pdf: PdfDocument): Promise<string> {
  if (typeof document === "undefined") return "";

  const [tesseractMod, workerUrlMod, coreUrlMod, fraUrlMod] = await Promise.all([
    import("tesseract.js"),
    import("tesseract.js/dist/worker.min.js?url"),
    import("tesseract.js-core/tesseract-core-simd-lstm.wasm.js?url"),
    import("@tesseract.js-data/fra/4.0.0_best_int/fra.traineddata.gz?url")
  ]);
  const tesseract = tesseractMod.default ?? tesseractMod;
  const fraResponse = await fetch(fraUrlMod.default);
  if (!fraResponse.ok) {
    throw new Error("Modèle de lecture français introuvable");
  }

  const worker = await tesseract.createWorker(
    [{ code: "fra", data: new Uint8Array(await fraResponse.arrayBuffer()) }],
    tesseract.OEM.LSTM_ONLY,
    {
      workerPath: workerUrlMod.default,
      corePath: coreUrlMod.default,
      workerBlobURL: false
    }
  );

  try {
    await worker.setParameters({
      tessedit_pageseg_mode: tesseract.PSM.AUTO,
      preserve_interword_spaces: "1",
      user_defined_dpi: "150"
    });

    const parts: string[] = [];
    const pageCount = Math.min(pdf.numPages, OCR_MAX_PAGES);
    for (let i = 1; i <= pageCount; i++) {
      const canvas = await renderPdfPage(pdf, i);
      if (!canvas) continue;
      const { data } = await worker.recognize(canvas);
      canvas.width = 0;
      canvas.height = 0;
      const pageText = data.text?.trim();
      if (pageText) parts.push(pageText);
    }
    return parts.join("\n\n").trim();
  } finally {
    await worker.terminate();
  }
}

async function extractTextFromPdf(fileBuffer: ArrayBuffer): Promise<string> {
  ensurePdfWorker();
  const loadingTask = pdfjsLib.getDocument({ data: fileBuffer.slice(0) });
  try {
    const pdf = await loadingTask.promise;
    const text = await readPdfTextLayer(pdf);
    if (!isExtractedContentInsufficient(text)) {
      return text;
    }

    try {
      const recognized = await recognizePdfPages(pdf);
      if (!isExtractedContentInsufficient(recognized)) {
        return recognized;
      }
    } catch (ocrError) {
      console.error("Erreur lors de la lecture du PDF:", ocrError);
    }

    return text;
  } finally {
    await loadingTask.destroy();
  }
}

function readU16(view: DataView, offset: number): number {
  return view.getUint16(offset, true);
}

function readU32(view: DataView, offset: number): number {
  return view.getUint32(offset, true);
}

function findZipEocdOffset(bytes: Uint8Array): number {
  const minOffset = Math.max(0, bytes.length - 65557);
  for (let i = bytes.length - 22; i >= minOffset; i--) {
    if (
      bytes[i] === 0x50 &&
      bytes[i + 1] === 0x4b &&
      bytes[i + 2] === 0x05 &&
      bytes[i + 3] === 0x06
    ) {
      return i;
    }
  }
  return -1;
}

async function inflateRaw(data: Uint8Array): Promise<Uint8Array> {
  const copy = new ArrayBuffer(data.byteLength);
  new Uint8Array(copy).set(data);
  const stream = new Blob([copy]).stream().pipeThrough(new DecompressionStream("deflate-raw"));
  return new Uint8Array(await new Response(stream).arrayBuffer());
}

async function unzipEntry(buffer: ArrayBuffer, targetPath: string): Promise<Uint8Array | null> {
  const bytes = new Uint8Array(buffer);
  const view = new DataView(buffer);
  const eocd = findZipEocdOffset(bytes);
  if (eocd < 0) {
    return null;
  }

  const cdOffset = readU32(view, eocd + 16);
  const cdSize = readU32(view, eocd + 12);
  const cdEnd = Math.min(bytes.length, cdOffset + cdSize);
  let offset = cdOffset;

  while (offset + 46 <= cdEnd) {
    if (readU32(view, offset) !== 0x02014b50) {
      break;
    }
    const method = readU16(view, offset + 10);
    const compSize = readU32(view, offset + 20);
    const nameLen = readU16(view, offset + 28);
    const extraLen = readU16(view, offset + 30);
    const commentLen = readU16(view, offset + 32);
    const localOffset = readU32(view, offset + 42);
    const name = new TextDecoder("utf-8").decode(bytes.subarray(offset + 46, offset + 46 + nameLen));
    offset += 46 + nameLen + extraLen + commentLen;

    if (name !== targetPath) {
      continue;
    }

    const localNameLen = readU16(view, localOffset + 26);
    const localExtraLen = readU16(view, localOffset + 28);
    const dataStart = localOffset + 30 + localNameLen + localExtraLen;
    const compressed = bytes.subarray(dataStart, dataStart + compSize);
    if (method === 0) {
      return compressed;
    }
    if (method === 8) {
      return inflateRaw(compressed);
    }
    throw new Error("Compression ZIP non supportée pour ce document Word");
  }

  return null;
}

function decodeXmlEntities(text: string): string {
  return text
    .replace(/&#(\d+);/g, (_, code) => String.fromCharCode(Number(code)))
    .replace(/&#x([0-9a-fA-F]+);/g, (_, code) => String.fromCharCode(parseInt(code, 16)))
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'");
}

function wordXmlToText(xml: string): string {
  return decodeXmlEntities(
    xml
      .replace(/<w:tab\b[^/]*\/>/g, "\t")
      .replace(/<w:br\b[^/]*\/>/g, "\n")
      .replace(/<\/w:p>/g, "\n")
      .replace(/<[^>]+>/g, "")
  )
    .replace(/\u00a0/g, " ")
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

async function extractTextFromDocx(fileBuffer: ArrayBuffer): Promise<string> {
  const documentXml = await unzipEntry(fileBuffer, "word/document.xml");
  if (!documentXml) {
    throw new Error("Document Word invalide (word/document.xml introuvable)");
  }
  return wordXmlToText(new TextDecoder("utf-8").decode(documentXml));
}

async function analyzeBufferWithMistral(params: {
  fileBuffer: ArrayBuffer;
  fileName: string;
  mimeType: string;
  documentId: string;
  mode?: DocumentAnalysisMode;
}): Promise<DocumentAnalysisResult> {
  const { fileBuffer, fileName, mimeType, documentId, mode = "full" } = params;

  if (!fileBuffer.byteLength) {
    throw new Error("Le document récupéré est vide");
  }

  const fileType = getFileType(fileName, mimeType);
  let documentContent = await extractTextFromDocument(fileBuffer, fileType);

  if (!documentContent || documentContent.trim() === "") {
    documentContent = "[Le document semble être vide ou dans un format non lisible]";
  }

  if (mode === "concise" && isExtractedContentInsufficient(documentContent)) {
    throw new Error(
      "Impossible d'extraire le texte de ce document (PDF scanné ou Word .doc). Déposez un PDF texte ou un fichier .docx."
    );
  }

  const data = await postIa<{ text: string }>("/ia/document-summary", {
    fileName,
    content: documentContent,
    mode
  });
  const analysis = data?.text?.trim();
  if (!analysis) {
    throw new Error("Pas de réponse de l'assistant");
  }

  return {
    analysis,
    documentId,
    documentName: fileName,
    documentType: fileType,
    analysisDate: new Date().toISOString()
  };
}

/**
 * Extrait le texte d'un document selon son type
 */
async function extractTextFromDocument(fileBuffer: ArrayBuffer, fileType: string): Promise<string> {
  try {
    let extractedText = "";
    fileType = fileType.toLowerCase();

    if (fileType === "pdf") {
      if (!isPdfBuffer(fileBuffer)) {
        extractedText = "[Le fichier récupéré n'est pas un PDF valide]";
      } else {
        try {
          extractedText = await extractTextFromPdf(fileBuffer);
        } catch (pdfError) {
          console.error("Erreur lors de l'extraction du PDF:", pdfError);
          extractedText = "[Erreur lors de l'extraction du contenu PDF]";
        }
      }
    } else if (fileType === "docx") {
      try {
        extractedText = await extractTextFromDocx(fileBuffer);
      } catch (docxError) {
        console.error("Erreur lors de l'extraction du DOCX:", docxError);
        extractedText = "[Erreur lors de l'extraction du contenu Word]";
      }
    } else if (fileType === "doc") {
      extractedText =
        "[Format DOC: contenu non extractible dans le navigateur, utilisez un PDF ou un .docx]";
    } else if (fileType === "xlsx" || fileType === "xls") {
      extractedText =
        "[Format " +
        fileType.toUpperCase() +
        ": contenu non extractible directement dans le navigateur]";
    } else if (
      fileType === "csv" ||
      fileType === "txt" ||
      fileType === "text" ||
      fileType === "md" ||
      fileType === "json"
    ) {
      try {
        const decoder = new TextDecoder("utf-8");
        extractedText = decoder.decode(fileBuffer);
      } catch (e) {
        extractedText = "[Erreur lors de l'extraction du texte]";
      }
    } else if (fileType === "pptx") {
      extractedText = "[Format PowerPoint: contenu non extractible directement]";
    } else {
      extractedText = `[Format ${fileType} non pris en charge pour l'extraction de texte]`;
    }

    return extractedText || "[Extraction du contenu impossible]";
  } catch (error) {
    console.error("Erreur lors de l'extraction du contenu:", error);
    return `[Erreur lors de l'extraction: ${
      error instanceof Error ? error.message : "Erreur inconnue"
    }]`;
  }
}

export async function analyzeDocumentWithMistral(document: {
  id: string;
  nom: string;
  mimeType: string;
  date: string;
}): Promise<DocumentAnalysisResult> {
  try {
    const documentUrl = `${URL_DATA()}/document/${document.id}?&access_token=${auth.token}`;
    const response = await fetch(documentUrl);
    if (!response.ok) {
      throw new Error(
        `Impossible de récupérer le document (${response.status}: ${response.statusText})`,
      );
    }

    const fileBuffer = await response.arrayBuffer();
    return await analyzeBufferWithMistral({
      fileBuffer,
      fileName: document.nom,
      mimeType: document.mimeType,
      documentId: document.id,
      mode: "full",
    });
  } catch (error) {
    console.error("Erreur lors de l'analyse avec Mistral:", error);
    return {
      analysis: `Une erreur est survenue lors de l'analyse: ${
        error instanceof Error ? error.message : "Erreur inconnue"
      }`,
      documentId: document.id,
      documentName: document.nom,
      documentType: getFileType(document.nom, document.mimeType),
      analysisDate: new Date().toISOString(),
    };
  }
}

export async function analyzeFileWithMistral(
  file: File,
  mode: DocumentAnalysisMode = "concise",
): Promise<DocumentAnalysisResult> {
  const fileType = getFileType(file.name, file.type);

  if (!LOCAL_ANALYSIS_TYPES.has(fileType)) {
    throw new Error("Seuls les fichiers PDF et Word (.docx, .doc) sont acceptés");
  }

  if (file.size > MAX_FILE_SIZE_BYTES) {
    throw new Error("Le fichier est trop volumineux (maximum 20 Mo)");
  }

  const fileBuffer = await file.arrayBuffer();
  if (fileType === "pdf" && !isPdfBuffer(fileBuffer)) {
    throw new Error("Le fichier sélectionné n'est pas un PDF valide");
  }
  if (fileType === "docx" && !isZipBuffer(fileBuffer)) {
    throw new Error("Le fichier sélectionné n'est pas un document Word valide");
  }
  if (fileType === "doc" && !isOleBuffer(fileBuffer)) {
    throw new Error("Le fichier sélectionné n'est pas un document Word valide");
  }

  return analyzeBufferWithMistral({
    fileBuffer,
    fileName: file.name,
    mimeType: file.type || getMimeTypeForFileType(fileType, file.name),
    documentId: "local",
    mode,
  });
}
